import { professionalFinding } from "@/lib/assessmentLogic";

/*
  Phase 8D — Professional Readiness Findings (client-facing).

  Replaces the previous Section 11 "Consultant Review Notes" surface on
  /report. This component NEVER renders raw consultantNotes, raw evidence
  notes, provider free-text comments, drafting notes, or internal reasoning.
  Every string produced here is a deterministic synthesis of structured
  evidence fields (answer, applicability, canonical state, verification,
  domain).

  The consultant/owner raw notes remain untouched in localStorage and in
  any backend record — this component only controls the client-facing
  output layer.
*/
export function ReportProfessionalFindings({ result }) {
  const items = Array.isArray(result?.evidenceItems) ? result.evidenceItems : [];

  // Material items = anything that carries a readiness signal a client
  // needs to see: missing / needs review / requires clarification.
  const material = items.filter(
    (i) => i.answer === "missing" || i.answer === "review" || i.answer === "unsure"
  );

  const missing = items.filter((i) => i.answer === "missing").length;
  const review = items.filter((i) => i.answer === "review").length;
  const clarify = items.filter((i) => i.answer === "unsure").length;
  const applicable = items.filter(
    (i) => i.answer === "current" || i.answer === "review"
  ).length;

  const overview =
    result?.manualReview
      ? "Automated categorisation is on hold pending manual consultant review. A synthesised readiness finding cannot be issued for this provider until manual review is complete."
      : `Across ${items.length} evidence items assessed, ${missing} were reported as missing, ${review} require documentation review, and ${clarify} require clarification before a readiness judgement can be issued. ${applicable} items were reported as in place or in preparation and can be spot-verified during the next internal audit cycle.`;

  return (
    <div className="space-y-4" data-testid="report-professional-findings">
      <p className="text-sm leading-relaxed text-muted-foreground">
        Deterministic professional readiness findings synthesised from the
        structured evidence fields recorded during this readiness review.
        These findings are consultancy readiness signals only — not audit
        determinations, legal opinions, regulator outcomes, or registration
        guarantees.
      </p>
      <p
        className="text-sm leading-relaxed text-foreground"
        data-testid="report-professional-findings-overview"
      >
        {overview}
      </p>

      {!result?.manualReview && (
        <div className="overflow-hidden rounded-sm border border-border bg-card">
          <ul className="divide-y divide-border">
            {material.length === 0 ? (
              <li
                className="px-4 py-3 text-sm text-muted-foreground"
                data-testid="report-professional-findings-empty"
              >
                No material readiness findings — every evidence item was
                reported as in place. Spot verification is recommended at the
                next internal audit cycle.
              </li>
            ) : (
              material.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-col gap-1 px-4 py-3"
                  data-testid={`report-professional-finding-${item.id}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {String(item.number).padStart(2, "0")} · {item.domain}
                    </span>
                    <span className="font-display text-[15px] font-medium leading-tight text-foreground">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-foreground/80">
                    {professionalFinding(item)}
                  </p>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
